/**
 * Persistance du cache de lecture (`ARCHITECTURE_V2.md` §3.2, étape 2 du §8).
 *
 * 🔴 **Le cache des données appartient à TanStack Query, et à lui seul.** C'est
 * la décision la plus importante du hors ligne, et elle se lit en creux dans ce
 * fichier autant que dans `public/sw.js` : le service worker ne met **aucune**
 * réponse `/api/*` en cache. Deux caches sur les mêmes données feraient deux
 * vérités, dont aucune ne saurait qu'elle a tort — le service worker servirait
 * une journée périmée pendant que TanStack Query en tiendrait une autre. On
 * **persiste** donc le cache existant plutôt que d'en créer un second.
 *
 * ⚠️ **Ce module ne persiste que des lectures.** Les mutations en attente sont
 * un tout autre sujet : TanStack Query les met déjà en pause hors ligne, mais
 * **en mémoire seulement** — elles ne survivent ni à la fermeture de
 * l'application ni au redémarrage de l'appareil (constaté à l'étape 1 de la
 * V2). C'est précisément ce que la file de l'étape 3 viendra tenir, en
 * IndexedDB. Ne pas essayer de le résoudre ici.
 *
 * ⚠️ **Aucune dépendance native, aucun script d'installation.** Les deux
 * paquets employés (`@tanstack/query-persist-client-core`, `idb-keyval`)
 * satisfont les contraintes héritées de la V1 — le VPS installe en
 * `--ignore-scripts` depuis l'étape 3, et aucune chaîne de compilation n'y est
 * installée. Vérifié dans les métadonnées npm, pas supposé.
 */

import type { PersistedClient, Persister } from '@tanstack/query-persist-client-core';
import { persistQueryClient } from '@tanstack/query-persist-client-core';
import type { Query, QueryClient } from '@tanstack/react-query';
import { createStore, del, get, set } from 'idb-keyval';

import { buildVersion } from './buildVersion';
import { browserToday, shiftDate } from './dates';

/* ==========================================================================
 * Stockage
 * ======================================================================= */

/**
 * Base IndexedDB dédiée.
 *
 * ⚠️ **IndexedDB et non `localStorage`** : le §3.3 l'exige pour la file, et la
 * raison vaut déjà ici — `localStorage` est synchrone (il bloque le fil
 * principal à chaque écriture, sur le chemin critique des 15 secondes) et
 * plafonné à quelques mégaoctets. Le journal d'une semaine y tiendrait, mais la
 * marge n'existerait plus.
 *
 * ⚠️ Base **distincte** de celle que la file de l'étape 3 ouvrira : purger le
 * cache de lecture à la déconnexion ne doit pas emporter des opérations non
 * encore acquittées.
 */
const magasin = createStore('nutri-cache', 'query');

const CLE = 'client';

/**
 * ⚠️ **Aucune sérialisation JSON.** `idb-keyval` s'appuie sur l'algorithme de
 * clonage structuré du navigateur, qui stocke l'objet tel quel : un aller-retour
 * `JSON.stringify`/`parse` sur tout le cache à chaque écriture coûterait du
 * temps de fil principal pour rien. Les DTO ne contiennent que des valeurs
 * clonables — nombres, chaînes, tableaux, objets nus.
 */
const persisteur: Persister = {
  persistClient: (client: PersistedClient) => set(CLE, client, magasin),
  restoreClient: () => get<PersistedClient>(CLE, magasin),
  removeClient: () => del(CLE, magasin),
};

/* ==========================================================================
 * Ce qui est persisté — liste blanche
 * ======================================================================= */

/**
 * 🔴 **Liste blanche, jamais liste noire.** Même discipline que
 * `shared/syncable.ts` à l'étape 1, et pour la même raison : une requête
 * ajoutée demain ne doit pas se retrouver persistée **par défaut**, sans que
 * personne n'ait décidé qu'elle devait l'être. Une clé inconnue n'est pas
 * persistée, un point c'est tout.
 *
 * Chaque racine porte le motif de son admission — ne pas savoir le remplir,
 * c'est constater que la requête n'a rien à faire ici.
 */
const RACINES_PERSISTEES: Readonly<Record<string, string>> = {
  // Sans elle, l'application ne s'ouvre pas hors ligne : la garde interroge
  // `/api/auth/status` à chaque montage (`screens/auth/AuthGate.tsx`).
  auth: "État d'authentification — la garde ne peut pas s'ouvrir sans lui",
  // Le cœur du contrat §3.1 : la journée courante et les journées récentes.
  day: 'Journal — fenêtre J−7 → J+1, cf. `dansLaFenetre`',
  templates: 'Modèles de journée — petits, et le journal les affiche',
  planning: 'Récurrence hebdomadaire et surcharges',
  'quick-access': "Favoris, récents, repas — de quoi remplir l'écran d'ajout",
  foods: 'Base personnelle — le §3.1 la veut disponible sans réseau',
  food: "Fiche d'un aliment, lue à la ré-édition d'une entrée",
  recipes: 'Recettes — même statut que la base personnelle',
  'recipe-tags': 'Filtre des recettes',
  /*
   * 🔴 **La réplique du §3.1** — c'est elle qui rend la recherche d'exercices
   * possible sans réseau (V2, étape 4). Une seule entrée, la liste entière,
   * filtrée en mémoire par `lib/localSearch.ts` ; il n'y a **pas** de clé par
   * terme de recherche, et c'est tout le sujet : un terme jamais tapé en ligne
   * n'aurait rien à restaurer.
   *
   * ⚠️ ≈ 300 Ko pour 873 lignes, mesuré — les instructions (571 Ko à elles
   * seules) ne sont **pas** dans cette liste, elles vivent sous `exercise`.
   */
  exercises: "Référentiel d'exercices — la réplique que la recherche filtre hors ligne",
  /*
   * ⚠️ Le détail d'un exercice, **une clé par exercice consulté**. C'est la
   * politique des images (§1.1 : « mises en cache à la consultation »),
   * appliquée au texte : on ne réplique pas 571 Ko d'instructions pour 873
   * exercices dont on en fait dix. Le préchargement ciblé d'une routine
   * viendra à l'étape 5.
   */
  exercise: "Détail d'un exercice — mis en cache à la consultation, comme ses images",
  /*
   * 🔴 **La réplique des routines** (V2, étape 5) — et ici l'**arbre entier**,
   * contrairement aux exercices dont les instructions restent au détail. Ce
   * n'est pas une inconséquence : c'est la même décision sur un autre volume.
   * Les instructions du jeu pèsent 571 Ko ; une routine de huit lignes pèse
   * ≈ 1,5 Ko, donc trente routines tiennent sous 50 Ko — moins que la base
   * personnelle d'aliments.
   *
   * ⚠️ Et la salle est précisément l'endroit où il n'y a pas de réseau : une
   * routine qu'il faudrait « avoir ouverte une fois en ligne » ne tiendrait pas
   * la promesse du §3.1.
   */
  routines: 'Routines — la réplique que la recherche filtre hors ligne, arbre compris',
  /*
   * 🔴 **C'est cette ligne et la suivante qui tiennent le critère du §8.6** —
   * « une séance complète en mode avion, **application fermée et rouverte au
   * milieu**, remontée intégralement ».
   *
   * Le raisonnement mérite d'être écrit, parce qu'il n'est pas celui des trois
   * autres domaines. Ailleurs, le cache persisté n'est qu'une **copie** de ce
   * que le serveur possède déjà : le perdre coûte un aller-retour. Ici, une
   * séance commencée hors ligne **n'existe nulle part ailleurs** que dans la
   * file (qui est une file d'opérations, pas un état lisible) et dans ce
   * cache. C'est donc la seule chose qui permette à l'écran de se reconstituer
   * après une fermeture, et c'est pour ça que l'écran de séance écrit son
   * aperçu optimiste **dans le cache** (`setQueryData`) plutôt que dans un
   * `useState`.
   *
   * ⚠️ La liste est bornée à trente séances par le serveur (≈ 90 Ko) : la seule
   * table de la V2 qui ne soit pas répliquée en entier, parce qu'une séance
   * s'ajoute quatre fois par semaine — ≈ 1 000 à cinq ans (§6.1).
   */
  sessions: 'Séances récentes — la fenêtre de trente que le serveur borne',
  /*
   * ⚠️ **La séance en cours, une clé par séance.** Elle double la précédente
   * volontairement : la liste peut la perdre (trente séances plus récentes),
   * celle-ci pas. Et c'est celle-là que l'écran relit au redémarrage.
   */
  session: 'Une séance — celle qu’on est en train de faire, et qui doit survivre à une fermeture',
  /*
   * ⚠️ **Le dernier passage, une clé par exercice**, préchargé à l'ouverture
   * d'une séance (§4.5). C'est la politique du détail d'un exercice et de ses
   * images : mis en cache à la consultation, parce que c'est **en salle** qu'on
   * en a besoin, c'est-à-dire là où il n'y a pas de réseau.
   */
  'exercise-last': 'Dernier passage sur un exercice — préchargé à l’ouverture de la séance',
  /*
   * ⚠️ **L'historique d'un exercice, une clé par exercice consulté.** Même
   * politique que son détail et que ses images : mis en cache **à la
   * consultation**, parce qu'on ouvre une fiche pour la lire — souvent en
   * salle, c'est-à-dire là où il n'y a pas de réseau. Le serveur le borne à
   * trente séances, donc l'entrée reste petite.
   */
  'exercise-history': 'Historique d’un exercice — mis en cache à la consultation, comme sa fiche',
  weight: 'Pesées, résumé et série du graphique',
  'weight-goals': 'Historique des objectifs de poids',
  notifications: 'Réglages des trois notifications, et le fuseau réglé',
  /*
   * ⚠️ **Persistée, alors même que sa source est un tiers** — ce qui la
   * distingue de `food-search`, exclue juste en dessous pour cette raison
   * exacte. La différence est le sens de la dépendance : `food-search`
   * *interroge* Open Food Facts au moment où l'écran s'affiche, donc la
   * persister ferait croire à une disponibilité hors ligne qui n'existe pas.
   * Ici le tiers a déjà été interrogé, il y a au plus trente minutes, **par le
   * serveur** ; ce qu'on persiste est le résultat rangé en base, exactement au
   * même titre qu'une pesée.
   *
   * ⚠️ Une seule entrée, quelques dizaines de kilo-octets : quatorze jours
   * d'activités et de bien-être, sans les `payload` sources que la route ne
   * renvoie pas.
   */
  intervals: 'Activités et santé importées — la fenêtre de 14 jours déjà rangée en base',
  /*
   * 🔴 **Les trois écrans du tableau de bord** (V2, étape 9). Ils sont
   * persistés pour la raison la plus simple qui soit : ce sont des **lectures
   * d'un historique déjà écrit**, donc rien ne peut y être perdu et tout y est
   * relisible hors ligne. C'est le critère de la liste blanche — « cette
   * requête a-t-elle des données ? » — et il est ici trivialement vrai.
   *
   * ⚠️ **Une clé par plage** (`['stats-nutrition', '90d']`), donc au plus
   * quatre entrées par écran. Ce n'est pas la mécanique refusée à l'étape 4
   * (une clé par terme de recherche, dont un mot jamais tapé n'a rien à
   * restaurer) : les plages sont un ensemble **fermé de quatre valeurs**, et
   * chacune est atteignable d'un tap. Celle qu'on regarde est celle qui a été
   * persistée.
   *
   * ⚠️ **Le poids est le seul à ne PAS avoir sa racine ici**, et c'est normal :
   * la projection voyage dans `WeightResponse`, donc sous la racine `weight`
   * qui est persistée depuis l'étape 2. L'étape n'a rien eu à ajouter pour
   * elle.
   */
  'stats-nutrition': 'Statistiques nutrition — une lecture d’historique, relisible hors ligne',
  'stats-training': 'Statistiques musculation — idem, et la salle n’a pas de réseau',
  'stats-health': 'Statistiques santé et croisements — idem',
};

/**
 * Ce qui est **délibérément** hors du cache persisté, avec son motif.
 *
 * ⚠️ Cette table n'a aucun effet à l'exécution — la liste blanche suffit à
 * exclure. Elle existe pour que le refus soit **écrit** plutôt que déduit d'une
 * absence, et qu'on ne l'ajoute pas un jour par réflexe.
 */
export const RACINES_EXCLUES: Readonly<Record<string, string>> = {
  'food-search':
    'Recherche unifiée : sa moitié Open Food Facts dépend d’un tiers (§5.2). ' +
    'La persister ferait croire à une disponibilité que le §3.1 annonce ' +
    'justement comme absente hors ligne.',
};

/**
 * La fenêtre du §3.1 : J−7 → J+1, dans le fuseau **de l'appareil**.
 *
 * 🔴 **C'est ce qui borne le cache par construction.** Sans elle, une journée
 * consultée s'y ajouterait et n'en sortirait jamais : après un an d'usage
 * quotidien, on persisterait 365 journées à chaque écriture du cache. La
 * fenêtre est donc à la fois le contrat annoncé à l'utilisateur et le garde-fou
 * de volume.
 *
 * ⚠️ **Le fuseau de l'appareil, comme partout dans le front** (retours de
 * l'étape 8) : « aujourd'hui » sur un écran est le jour de l'appareil qu'on
 * tient. Le serveur garde sa propre notion du jour pour les notifications.
 */
function dansLaFenetre(date: string): boolean {
  const aujourdhui = browserToday();
  return date >= shiftDate(aujourdhui, -7) && date <= shiftDate(aujourdhui, 1);
}

/**
 * 🔴 **Le critère est « cette requête a-t-elle des données ? », et surtout pas
 * « a-t-elle réussi ? ». Trouvé au banc, et c'était un défaut de fond.**
 *
 * La première version filtrait sur `status === 'success'`. Le raisonnement
 * semblait juste — ne pas coucher un état d'échec sur le disque — mais il
 * ignorait un comportement de TanStack Query : une requête qui possède déjà des
 * données **passe en `error`** quand son rafraîchissement échoue. Ses données
 * restent parfaitement là, seul son statut change.
 *
 * Conséquence observée, et elle est vicieuse : hors ligne, chaque requête
 * relancée échouait, perdait son droit d'être persistée, et le cache **se
 * réécrivait sans elle**. Le cache se vidait donc tout seul au fil des
 * navigations — l'ouverture suivante ne trouvait plus l'état
 * d'authentification, et l'application affichait « API injoignable » alors
 * qu'elle venait de s'ouvrir correctement une minute plus tôt. **Le hors ligne
 * se détruisait d'autant plus vite qu'on s'en servait.**
 *
 * Ce qu'on persiste, ce sont des **données**, pas un verdict. Une donnée
 * périmée vaut infiniment mieux que rien quand il n'y a pas de réseau — et
 * c'est très exactement la promesse du §3.1.
 *
 * ⚠️ Une requête sans données — jamais aboutie, ou en vol — reste écartée : elle
 * n'a rien à restaurer.
 */
function aPersister(query: Query): boolean {
  if (query.state.data === undefined) return false;

  const racine = query.queryKey[0];
  if (typeof racine !== 'string') return false;
  if (!(racine in RACINES_PERSISTEES)) return false;

  if (racine === 'day') {
    const date = query.queryKey[1];
    return typeof date === 'string' && dansLaFenetre(date);
  }

  /*
   * ⚠️ `['auth', 'account']` porte la liste des passkeys. Elle n'a aucune
   * utilité hors ligne — on ne gère pas ses appareils sans réseau — et il n'y a
   * pas de raison de la coucher sur le disque. Seul `status` est retenu, parce
   * que sans lui la garde ne s'ouvre pas.
   */
  if (racine === 'auth') return query.queryKey[1] === 'status';

  return true;
}

/* ==========================================================================
 * Démarrage et purge
 * ======================================================================= */

/**
 * Durée de vie du cache persisté.
 *
 * Alignée sur la fenêtre du §3.1 : au-delà d'une semaine sans ouvrir
 * l'application, ce qu'on aurait restauré serait de toute façon hors fenêtre.
 * ⚠️ Ce plafond porte sur **le cache entier** — c'est l'ancienneté de la
 * dernière écriture, pas celle de chaque requête.
 */
const AGE_MAX_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Plafond d'attente de la restauration au démarrage.
 *
 * ⚠️ **On attend, et il faut expliquer pourquoi.** Rendre l'application avant
 * que le cache ne soit restauré la ferait démarrer avec un cache vide : hors
 * ligne, chaque écran partirait en requête, échouerait, afficherait son erreur,
 * puis serait remplacé par les données restaurées. L'utilisateur verrait donc
 * un écran cassé avant l'écran correct — l'inverse exact de ce que le hors
 * ligne promet.
 *
 * Le plafond couvre le cas où IndexedDB ne répond pas (navigation privée,
 * quota, base verrouillée par un autre onglet) : mieux vaut une application qui
 * démarre sans cache qu'une application qui ne démarre pas.
 */
const ATTENTE_MAX_MS = 3000;

let arreter: (() => void) | null = null;

/**
 * Branche la persistance et **attend la restauration**.
 *
 * Renvoie sans jamais lever : un stockage indisponible dégrade le hors ligne,
 * il ne doit pas empêcher l'application de s'ouvrir.
 */
export async function startPersistence(queryClient: QueryClient): Promise<void> {
  try {
    const [unsubscribe, restaure] = persistQueryClient({
      queryClient,
      persister: persisteur,
      maxAge: AGE_MAX_MS,
      /*
       * 🔴 La clé de version du §3.2. Quand elle change, `persistQueryClient`
       * jette le cache au lieu de l'hydrater : c'est ce qui empêche un bundle
       * neuf de lire des DTO de l'ancienne forme.
       */
      buster: buildVersion(),
      dehydrateOptions: { shouldDehydrateQuery: aPersister },
    });

    arreter = unsubscribe;

    await Promise.race([
      restaure,
      new Promise<void>((resolve) => setTimeout(resolve, ATTENTE_MAX_MS)),
    ]);
  } catch (cause) {
    console.warn('[nutri] Cache hors ligne indisponible.', cause);
  }
}

/**
 * Purge le cache persisté — appelé à la déconnexion.
 *
 * 🔴 **Il contient le journal alimentaire et l'historique de poids** : il ne
 * doit pas survivre à une session qui n'a plus de propriétaire. Même règle que
 * `forgetTabs()` (`lib/tabMemory.ts`), posée au troisième tour de l'étape 10.
 *
 * ⚠️ **L'ordre d'appel compte, et il est tenu par construction plutôt que par
 * chance.** L'abonnement de `persistQueryClient` réécrit le cache dès qu'il
 * change, avec un léger différé : une écriture peut donc partir *après* cette
 * purge. C'est sans conséquence à une condition — que le cache mémoire ait
 * **déjà** été vidé quand on arrive ici. `AccountSection` le fait
 * (`removeQueries`), et ce qui reste alors est `['auth', 'status']`, c'est-à-dire
 * exactement ce qu'on veut voir persisté : « personne n'est connecté ».
 */
export async function purgePersistedCache(): Promise<void> {
  try {
    await persisteur.removeClient();
  } catch (cause) {
    console.warn('[nutri] Purge du cache hors ligne impossible.', cause);
  }
}

/**
 * Détache l'abonnement de persistance.
 *
 * ⚠️ **Toujours sans appelant après l'étape 3, et c'est un constat, pas un
 * oubli.** Il avait été exporté pour que la file puisse suspendre l'écriture du
 * cache pendant un drainage ; elle n'en a pas eu besoin, et il vaut mieux dire
 * pourquoi que de l'appeler pour justifier son existence.
 *
 * Un drainage ne réécrit pas le cache de lecture : il émet des requêtes, puis
 * **invalide** les racines concernées (`main.tsx`), ce qui fait repartir des
 * lectures normales. Les écritures de persistance qui en découlent sont
 * exactement celles qu'on veut coucher sur le disque — l'état que le serveur
 * vient de confirmer. Suspendre la persistance pendant ce temps ne protégerait
 * de rien et laisserait, en cas de fermeture au mauvais moment, un cache plus
 * ancien que la file qui l'accompagne.
 *
 * Conservé exporté : la séance en direct (étape 6) écrira beaucoup plus souvent,
 * et c'est là que la question pourra se reposer pour de bon.
 */
export function stopPersistence(): void {
  arreter?.();
  arreter = null;
}
